import { get } from 'aws-amplify/api';

// Same API and Lambda as the rest of /core/limits, so the history reads with
// the same session and the same hierarchy rules as the limits themselves.
const API_NAME = 'coreuser';
const PATH = '/core/limits/events';

/**
 * Refused stakes, newest first.
 *
 * Rows are written by the casino wallet whenever a bet is turned away for
 * hitting a limit, and they expire after 30 days. Nothing here can change a
 * limit — this is the read side.
 *
 * @param {Object} [options]
 * @param {number} [options.days]             window to read, capped at the 30-day retention
 * @param {number|string} [options.from]      epoch ms or ISO, overrides days
 * @param {number|string} [options.to]        epoch ms or ISO
 * @param {string} [options.username]         one player
 * @param {string} [options.limitOwner]       "country:countrybeirut", "system:global-fallback"
 * @param {string} [options.productProvider]  "sports#gr8", "casino#pm8"
 * @param {string[]} [options.breachType]     above-maximum | below-minimum | match-total-exceeded | forbidden-amount
 * @param {string[]} [options.limitSource]    limits-table | match-exposure | provider-hardcoded | ...
 * @param {string[]} [options.product]        casino | sports
 * @param {string[]} [options.provider]       pm8 | databetrix | betinovio | rebeltech | gr8 | betconstruct | turbostars
 * @param {string[]} [options.currency]
 * @param {string[]} [options.sourceMarker]   the exact code branch that refused the bet
 * @param {boolean} [options.configurable]    true = only limits an admin can change in the panel
 * @param {number} [options.pageSize]
 * @param {string} [options.cursor]           from a previous response
 * @returns {Promise<{success: boolean, events: Array, cursor: string|null, hasMore: boolean, code?: string, error?: string}>}
 */
export const fetchLimitEvents = async (options = {}) => {
  const queryParams = {};

  const setValue = (key, value) => {
    if (value === undefined || value === null || value === '') return;
    // Repeated filters travel as one comma-separated value; the Lambda splits
    // them back apart and treats them as an OR within that field.
    const flattened = Array.isArray(value) ? value.filter(Boolean).join(',') : value;
    if (flattened === '' ) return;
    queryParams[key] = String(flattened);
  };

  setValue('days', options.days);
  setValue('from', options.from);
  setValue('to', options.to);
  setValue('username', options.username);
  setValue('limitOwner', options.limitOwner);
  setValue('productProvider', options.productProvider);
  setValue('breachType', options.breachType);
  setValue('limitSource', options.limitSource);
  setValue('limitType', options.limitType);
  setValue('product', options.product);
  setValue('provider', options.provider);
  setValue('currency', options.currency);
  setValue('sourceMarker', options.sourceMarker);
  setValue('pageSize', options.pageSize);
  setValue('cursor', options.cursor);

  if (options.configurable === true || options.configurable === false) {
    queryParams.configurable = String(options.configurable);
  }

  try {
    const response = await get({
      apiName: API_NAME,
      path: PATH,
      options: { queryParams }
    }).response;

    const data = await response.body.json();
    return {
      success: data.success !== false,
      events: data.events || [],
      cursor: data.cursor || null,
      hasMore: Boolean(data.hasMore),
      range: data.range || null,
      error: data.error
    };
  } catch (error) {
    console.error('Error fetching limit events:', error);

    // The table is created out of band, so "not deployed yet" has to reach the
    // UI as its own state rather than as a generic failure — the answer is to
    // run the create script, not to retry.
    const body = await readErrorBody(error);

    return {
      success: false,
      events: [],
      cursor: null,
      hasMore: false,
      code: body?.code,
      error: body?.error || error.message
    };
  }
};

/** Amplify wraps the API Gateway response, so the useful part is a layer down. */
const readErrorBody = async (error) => {
  try {
    const response = error?.response;
    if (!response) return null;
    if (typeof response.body === 'string') return JSON.parse(response.body);
    if (response.body?.json) return await response.body.json();
    return response.body || null;
  } catch {
    return null;
  }
};
